import React, { useState } from "react";
import { MapPin, ExternalLink } from "lucide-react";

/**
 * NearbyPlacesFinder — "Find care near you" card on /resources.
 *
 * Caregiver types their address (or city/ZIP), then taps a care type —
 * we open a pre-built Google Maps search in a new tab ("adult day
 * programs near 123 Main St, Austin TX"). No API key, works worldwide,
 * and Maps handles hours/reviews/directions better than we ever could.
 * Address persists in localStorage so they only type it once.
 */
const PLACE_TYPES = [
    { key: "adult_day",    label: "Adult day programs",   query: "adult day programs" },
    { key: "respite",      label: "Respite care",         query: "respite care" },
    { key: "home_health",  label: "Home health agencies", query: "home health agencies" },
    { key: "neurology",    label: "Neurologists",         query: "neurologist" },
    { key: "geriatrician", label: "Geriatricians",        query: "geriatrician" },
    { key: "memory_care",  label: "Memory care",          query: "memory care facilities" },
    { key: "support",      label: "Support groups",       query: "dementia caregiver support groups" },
    { key: "elder_law",    label: "Elder law attorneys",  query: "elder law attorney" },
];

const ADDR_KEY = "ml_finder_address";

export const NearbyPlacesFinder = () => {
    const [address, setAddress] = useState(() => {
        try { return localStorage.getItem(ADDR_KEY) || ""; } catch { return ""; }
    });

    const openMaps = (query) => {
        const near = address.trim();
        try { localStorage.setItem(ADDR_KEY, near); } catch { /* private mode */ }
        const q = near ? `${query} near ${near}` : `${query} near me`;
        window.open(`https://www.google.com/maps/search/${encodeURIComponent(q)}`, "_blank", "noopener");
    };

    return (
        <section className="ml-card p-5 md:p-6 space-y-4" data-testid="nearby-places-finder">
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center shrink-0">
                    <MapPin size={18} strokeWidth={1.7} />
                </div>
                <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Find care near you</p>
                    <h2 className="text-xl md:text-2xl font-heading font-medium text-[#12776F]">
                        What&apos;s close to your address?
                    </h2>
                    <p className="text-sm text-[#4C5551] mt-1 max-w-2xl">
                        Enter your address, city, or ZIP — then tap a care type to see nearby options
                        on a map with hours, reviews, and directions.
                    </p>
                </div>
            </div>

            <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="e.g. 123 Main St, Austin TX — or just a ZIP code"
                data-testid="nearby-finder-address"
                className="ml-input"
                autoComplete="street-address"
            />

            <div className="flex flex-wrap gap-2" data-testid="nearby-finder-types">
                {PLACE_TYPES.map((p) => (
                    <button
                        key={p.key}
                        type="button"
                        onClick={() => openMaps(p.query)}
                        data-testid={`nearby-finder-${p.key}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-medium border border-[#E2E8F0] bg-white text-[#4C5551] hover:bg-teal-soft hover:text-teal-dark hover:border-teal-light transition-colors"
                    >
                        {p.label}
                        <ExternalLink size={12} strokeWidth={2} className="opacity-60" />
                    </button>
                ))}
            </div>

            <p className="text-xs text-[#6E7A74]">
                Opens Google Maps in a new tab. {address.trim() ? "" : "No address? We'll search near your current location."}
            </p>
        </section>
    );
};

export default NearbyPlacesFinder;
